import React, { useState, useEffect, useCallback } from 'react';
import { 
    Calendar, RefreshCw, FileText, Users, 
    CheckCircle, Clock, TrendingUp 
} from 'lucide-react';
import { api } from '../../api';
import toast from 'react-hot-toast';
import '../../styles/HousekeepingAnalytics.css';

const HousekeepingAnalytics = ({ propertyId }) => {
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState([]);
    const [staffList, setStaffList] = useState([]);
    
    // Independent Analytics Filters
    const [filters, setFilters] = useState({
        startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Default to last 7 days
        endDate: new Date().toISOString().split('T')[0],
        employeeId: ''
    });

    // 1. Fetch available staff for the dropdown filter
    useEffect(() => {
        const loadStaff = async () => {
            if (!propertyId) return;
            try {
                // Using existing getWorkforce API to populate the staff dropdown
                const staff = await api.getWorkforce({ propertyId, role: 'Housekeeper' });
                if (staff && staff.users) {
                    setStaffList(staff.users);
                } else if (Array.isArray(staff)) {
                    setStaffList(staff);
                }
            } catch (error) {
                console.error("Could not load staff list", error);
            }
        };
        loadStaff();
    }, [propertyId]);

    // 2. Fetch the actual report data
    const fetchReport = useCallback(async () => {
        if (!propertyId) return;
        setLoading(true);
        try {
            const result = await api.getHousekeepingReport({
                propertyId,
                startDate: filters.startDate,
                endDate: filters.endDate,
                employeeId: filters.employeeId
            });
            
            setReportData(result || []); 
        } catch (err) {
            console.error(err);
            toast.error("Error loading analytics data");
            setReportData([]); 
        } finally {
            setLoading(false);
        }
    }, [propertyId, filters]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    // --- NEW: Helper to format duration strings (e.g., "210m / 3h 30m") ---
    const formatDuration = (val) => {
        const totalMins = Math.round(Number(val));
        if (!totalMins || isNaN(totalMins) || totalMins === 0) return '0m';

        if (totalMins < 60) {
            return `${totalMins}m`;
        }

        const hours = Math.floor(totalMins / 60);
        const mins = totalMins % 60;
        return `${totalMins}m / ${hours}h ${mins}m`;
    };

    // Helper to gracefully handle the API response
    const records = Array.isArray(reportData) ? reportData : (reportData.staffPerformance || []);
    
    // Calculate summary stats dynamically
    const totalCleaned = Array.isArray(reportData) 
        ? reportData.filter(r => r.task_status === 'COMPLETED').length 
        : (reportData.summary?.totalCleaned || 0);

    const calcAvgTime = () => {
        if (!Array.isArray(reportData)) return formatDuration(reportData.summary?.avgTime || 0);
        
        // Only calculate average for items that are COMPLETED and have a duration > 0
        const completed = reportData.filter(r => r.task_status === 'COMPLETED' && r.duration > 0);
        
        if (completed.length === 0) return '0m';
        
        const totalMins = completed.reduce((sum, r) => sum + Number(r.duration), 0);
        const avg = totalMins / completed.length;
        
        return formatDuration(avg);
    };

    return (
        <div className="hk-analytics-container">
            {/* --- Analytics Filter Bar --- */}
            <div className="hk-analytics-toolbar">
                <div className="hk-date-picker-group">
                    <div className="hk-input-with-icon">
                        <Calendar size={14} />
                        <label>From:</label>
                        <input 
                            type="date" 
                            value={filters.startDate} 
                            onChange={(e) => setFilters({...filters, startDate: e.target.value})} 
                        />
                    </div>
                    <span className="date-separator">to</span>
                    <div className="hk-input-with-icon">
                        <Calendar size={14} />
                        <label>To:</label>
                        <input 
                            type="date" 
                            value={filters.endDate} 
                            onChange={(e) => setFilters({...filters, endDate: e.target.value})} 
                        />
                    </div>
                    <div className="hk-input-with-icon">
                        <Users size={14} />
                        <select 
                            value={filters.employeeId} 
                            onChange={(e) => setFilters({...filters, employeeId: e.target.value})}
                        >
                            <option value="">All Staff</option>
                            {staffList.map(staff => (
                                <option key={staff.id} value={staff.id}>
                                    {staff.first_name} {staff.last_name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="hk-toolbar-actions">
                    <button className="hk-refresh-btn" onClick={fetchReport} disabled={loading}>
                        <RefreshCw size={18} className={loading ? 'spin' : ''} />
                        <span>Update Report</span>
                    </button>
                </div>
            </div>

            {/* --- Metric Overview Cards --- */}
            <div className="hk-stats-overview">
                <div className="hk-metric-card">
                    <div className="metric-icon bg-green-soft"><CheckCircle size={22} color="#10b981" /></div>
                    <div className="metric-content">
                        <span className="metric-label">Rooms Cleaned</span>
                        <span className="metric-value">{totalCleaned}</span>
                    </div>
                </div>
                
                <div className="hk-metric-card">
                    <div className="metric-icon bg-blue-soft"><Clock size={22} color="#3b82f6" /></div>
                    <div className="metric-content">
                        <span className="metric-label">Avg. Time / Room</span>
                        {/* Apply formatting here */}
                        <span className="metric-value" style={{ fontSize: '1.4rem' }}>{calcAvgTime()}</span>
                    </div>
                </div>

                <div className="hk-metric-card">
                    <div className="metric-icon bg-purple-soft"><TrendingUp size={22} color="#8b5cf6" /></div>
                    <div className="metric-content">
                        <span className="metric-label">Efficiency</span>
                        <span className="metric-value">{totalCleaned > 0 ? '100%' : '0%'}</span>
                    </div>
                </div>
            </div>

            {/* --- Data Table --- */}
            <div className="hk-report-card">
                <div className="card-header">
                    <h3>Performance Details</h3>
                </div>
                <div className="hk-table-container">
                    {records.length > 0 ? (
                        <table className="hk-analytics-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Room</th>
                                    <th>Housekeeper</th>
                                    <th>Status</th>
                                    <th>Duration</th>
                                </tr>
                            </thead>
                            <tbody>
                                {records.map((row, idx) => (
                                    <tr key={row.id || idx}>
                                        <td>{row.shift_date ? new Date(row.shift_date).toLocaleDateString() : 'N/A'}</td>
                                        <td className="font-bold">{row.room_number || row.roomsCount}</td>
                                        <td>{row.first_name ? `${row.first_name} ${row.last_name}` : row.name}</td>
                                        <td>
                                            <span className={`hk-status-pill ${row.task_status === 'COMPLETED' || row.roomsCount ? 'success' : 'neutral'}`}>
                                                {row.task_status || 'Completed'}
                                            </span>
                                        </td>
                                        {/* Apply formatting here as well for consistency */}
                                        <td>
                                            {formatDuration(row.duration || row.avgTime || 0)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="hk-empty-row">
                            <FileText size={40} color="#cbd5e1" style={{ margin: '0 auto 10px' }} />
                            <p>No historical records found for the selected filters.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default HousekeepingAnalytics;